import React, { useMemo } from "react";
import {
  endpoints,
  poster,
  fetcherGET,
  fetcher,
  fetcherSwr,
} from "../utils/axios";
import useSWR from "swr";

export function useGetAnalytics(args = {}) {
  const URL = args ? [endpoints.analytics.getInsights, { params: args }] : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcherSwr);

  const memoizedValue = useMemo(
    () => ({
      analytics: data?.data || null,
      isLoading,
      error,
      isValidating,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Same data as useGetAnalytics, fetched as two independent requests instead of
// one. The summary half (test counts, cases, patient/gender mix) is cheap; the
// interpretation half (interpreted totals, average time, status mix, centre map)
// carries the slow facet. Splitting them lets the KPI row render while the
// centre map is still loading. The two responses are merged back into the same
// object shape the dashboard already consumes, so callers only need to swap the
// hook and, if they want progressive rendering, read isLoadingInterpretation.
export function useGetAnalyticsSplit(args = {}) {
  const summaryURL = args ? [endpoints.analytics.getInsightsSummary, { params: args }] : null;
  const interpretationURL = args
    ? [endpoints.analytics.getInsightsInterpretation, { params: args }]
    : null;

  const summary = useSWR(summaryURL, fetcherSwr);
  const interpretation = useSWR(interpretationURL, fetcherSwr);

  const summaryData = summary.data?.data;
  const interpretationData = interpretation.data?.data;

  return useMemo(
    () => ({
      analytics:
        summaryData || interpretationData
          ? { ...(summaryData || {}), ...(interpretationData || {}) }
          : null,
      // Drives the KPI cards.
      isLoading: summary.isLoading,
      // Drives the centre map / interpretation panel only.
      isLoadingInterpretation: interpretation.isLoading,
      isValidating: summary.isValidating || interpretation.isValidating,
      error: summary.error || interpretation.error,
    }),
    [
      summaryData,
      interpretationData,
      summary.isLoading,
      interpretation.isLoading,
      summary.isValidating,
      interpretation.isValidating,
      summary.error,
      interpretation.error,
    ]
  );
}

export function useGetAnalyticsTables(args = {}) {
  const URL = args ? [endpoints.analyticsExport.getTables, { params: args }] : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcherSwr);

  const memoizedValue = useMemo(
    () => ({
      tables: data?.data || null,
      isLoading,
      error,
      isValidating,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetTimeseries(params = {}) {
  const URL = params ? [endpoints.analytics.getTimeseries, { params }] : null;

  const { data, error, isLoading, isValidating } = useSWR(URL, fetcherSwr);

  const memoizedValue = useMemo(
    () => ({
      timeSeries: data?.data || null,
      isLoading,
      error,
      isValidating,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
